/**
 * 云服务类别 产品图表页 导出数据Dialog
 *
 * @format
 */

import React, { useState, useEffect, useContext, useRef } from 'react'
import { _$, Dialog, Checkbox, Message } from '@king-design/react'
import NoDataComponent from 'src/components/NoData'
import styled from 'styled-components'
import { CountContext } from 'src/pages/MonitorCloud/MonitorCloudChart'
import { ebs_childtypes } from 'src/containers/MonitorCloud/MonitorCloudChart/MonitorChartList'
import { getQueryDataParams } from '../constants'
import { request, handleExportForm, handleUnit } from 'src/utils'
import API from 'src/api/ApiCloud'
import { dateTime } from 'src/config'
import dayjs from 'dayjs'

interface MonitorChartExportProps {
  visible: boolean
  rangeDate: [number, number]
  exportData: any[]
  closeExport: (_c: any, value: boolean) => void
  className?: string
}

const MonitorChartExport: React.FC<MonitorChartExportProps> = ({
  visible,
  rangeDate,
  exportData,
  closeExport,
  className
}) => {
  const {
    curProduct,
    nameSpace,
    instanceId,
    instanceName,
    childTypeValue,
    currentInstance,
    shardValue,
    regionName
  } = useContext(CountContext)
  const [handleData, setHandleData] = useState([])
  const [checked, setChecked] = useState([])
  const dialogRef = useRef<any>()

  // 初始化，展开所有监控项
  useEffect(() => {
    if (visible) {
      const data = exportData?.reduce((res, { appId, graphs }) => {
        const appIdGraphs = graphs.map(item => ({ ...item, appId }))
        return [...res, ...appIdGraphs]
      }, [])
      setHandleData(data)
    }
    setChecked([])
  }, [visible])

  // 全选/反选
  const toggleSelectAll = value => {
    if (value) {
      const all = handleData.map(({ graphId, title }) => `${graphId}-${title}`)
      setChecked(all)
    } else {
      setChecked([])
    }
  }

  // 提交
  const handeSubmit = async () => {
    const paramsList = []
    checked.forEach(item => {
      let findItem = null
      for (const index in exportData) {
        const { graphs } = exportData[index]
        findItem = graphs.find(({ graphId, title }) => item === `${graphId}-${title}`)
        if (findItem) {
          const findInstaceId = shardValue?.instanceId || currentInstance?.instanceId || instanceId
          const params = getQueryDataParams(
            findItem,
            findInstaceId,
            rangeDate,
            curProduct?.productType
          )
          paramsList.push({ ...params, title: findItem.title, unit: findItem.units })
          return false
        }
      }
    })
    // eslint-disable-next-line prefer-spread
    const delay = Math.max.apply(
      Math,
      paramsList.map(item => {
        if (curProduct?.productType === 17) {
          return parseInt(item.Period) || 60
        } else {
          return parseInt(item.interval) || 60
        }
      })
    )
    const productType = childTypeValue?.childType || curProduct?.productType
    const promiseList = paramsList.map(item => {
      return new Promise(resolve => {
        request(API.getMetricsData, {
          method: 'POST',
          data: {
            ...item,
            Namespace: ebs_childtypes.includes(productType) ? 'DBSC/EBS' : nameSpace,
            Period: delay
          },
          region: regionName
        }).then(res => {
          const result = []
          if (res?.data?.data?.length) {
            res?.data?.data.forEach(({ metricDesc, datapoints }) => {
              result.push({
                name: `${item.title}-${metricDesc}`,
                data: datapoints.map(({ timestamp, value }) => [
                  parseFloat(timestamp),
                  handleUnit(value, item.unit)
                ])
              })
            })
          } else {
            item.Metrics.forEach(({ MetricDesc }) => {
              result.push({
                name: `${item.title}-${MetricDesc}`,
                data: []
              })
            })
          }
          return resolve(result)
        })
      })
    })
    dialogRef?.current?.showLoading()
    const result: any[] = await Promise.all(promiseList)
    // const result: any[] = undefined
    dialogRef?.current?.hideLoading()
    if (result?.length) {
      const resultReduce = result.reduce((pre, item) => [...pre, ...item], [])
      const headers = [{ key: 'time', value: _$('时间') }].concat(
        resultReduce.map(({ name }) => ({
          key: name,
          value: name
        }))
      )
      let dataSource = null
      for (const index in resultReduce) {
        const { data } = resultReduce[index]
        let setDate = false
        if (!setDate && data?.length) {
          dataSource = data.map(([date]) => [dayjs(date * 1000).format(dateTime)])
          setDate = true
        }
      }
      if (dataSource?.length) {
        dataSource.forEach((item, index) => {
          const date = dayjs(item[0]).unix()
          for (const i in resultReduce) {
            let curCount = ''
            for (const j in resultReduce[i].data) {
              const [jDate, jCount] = resultReduce[i].data[j]
              if (date === jDate) {
                curCount = jCount
                break
              }
            }
            dataSource[index].push(curCount)
          }
        })
        const instaceName =
          shardValue?.instanceName || currentInstance?.instanceName || instanceName
        handleExportForm(headers, dataSource, instaceName)
        dialogRef?.current?.close()
        Message.success(_$('导出成功'))
      } else {
        dialogRef?.current?.close()
        Message.warning(_$('暂无数据'))
      }
    }
  }

  return (
    <Dialog
      mode="destroy"
      width={900}
      value={visible}
      onChangeValue={closeExport}
      title={_$('导出数据')}
      ok={handeSubmit}
    >
      <div className={className}>
        {handleData.length ? (
          <>
            <Checkbox
              indeterminate={checked.length > 0 && checked.length < handleData.length}
              value={checked.length === handleData.length}
              onChangeValue={toggleSelectAll}
            >
              {_$('全选')}
            </Checkbox>
            <hr />
            <div className="dialog-content">
              {handleData.map(({ graphId, title }) => (
                <div className="check-item" key={`${graphId}-${title}`}>
                  <Checkbox
                    name="languages"
                    trueValue={`${graphId}-${title}`}
                    value={checked}
                    onChangeValue={value => {
                      setChecked(value)
                    }}
                  >
                    {_$(title)}
                  </Checkbox>
                </div>
              ))}
            </div>
            <p className="dialog-bottom">
              *{_$('备注')}：{_$('同时导出多个监控项时按照最大周期聚合')}
            </p>
          </>
        ) : (
          <NoDataComponent />
        )}
      </div>
    </Dialog>
  )
}

export default styled(MonitorChartExport)`
  overflow-y: auto;
  .dialog-content {
    display: flex;
    flex-wrap: wrap;
    .check-item {
      padding-bottom: 10px;
      width: 33%;
    }
  }
  .dialog-bottom {
    margin: 10px 0 0 0;
  }
`
